/**
 * useAutoReload — silently update the tablet to a new build during idle.
 *
 * The elderly user cannot perform a hard-refresh. Without intervention, a
 * tablet running v1's JS will keep running v1 forever — even after we deploy
 * v2 — because:
 *   - Android Chrome's bfcache restores the tab from a memory snapshot on
 *     wake without re-fetching index.html, so Cache-Control: no-store does
 *     nothing in that path
 *   - In PWA standalone mode, the tab may never naturally navigate again
 *
 * This hook closes that gap. The server announces its BUILD_VERSION in the
 * WebSocket handshake (server_hello); the tablet stores the build it
 * loaded with; mismatch arms `isStale`; the page reloads at the next safe
 * moment.
 *
 * Safe moments, in priority:
 *   1. `pageshow` event with persisted=true → tab restored from bfcache.
 *      Bas's testing this week showed this is the dominant case for stale
 *      code in production. Reload immediately — the user JUST returned to
 *      the app and hasn't started interacting yet.
 *   2. `onCallEnd()` is called by the WS handler when a call ends. We wait
 *      5 s (give the user a beat to see the call-ended UI before the
 *      screen blinks), then reload.
 *   3. Idle ceiling: if the tablet has been stale for > IDLE_RELOAD_MS
 *      AND no call is currently active, reload immediately. Catches a
 *      tablet that's just sitting on the home screen for hours.
 *   4. Daily 04:00 local-time reload regardless of staleness. Defence in
 *      depth — catches edge cases where WS never reconnected after a
 *      deploy, so no server_hello ever surfaced a mismatch.
 *   5. Manual long-press refresh button on the idle screen (not in this
 *      hook — see `LongPressRefreshButton` in `pages/TabletApp.js`).
 *      Covers the one residual case the four automatic triggers can't:
 *      the *very first call on the same day as a deploy*. We
 *      deliberately do NOT add an automatic 5th trigger here — the
 *      obvious candidate (reload when `server_hello` reports a
 *      mismatch and the WS hasn't reached `ready` yet) would eject
 *      the user mid call-setup. They'd return to the idle screen
 *      after their tap and have to re-initiate the call, with no
 *      explanation of why — strictly worse than the stale-bundle UX
 *      for an elderly user. The other auto path (background poll of
 *      `/api/build_version` while idle) adds new HTTP traffic for a
 *      degradation that the existing four triggers + the button
 *      already cover. Keep this comment in lock-step with the button
 *      component if either side changes.
 *
 * NEVER reload during an active call — that would yank Berta mid-sentence.
 * The caller passes `isCallActiveRef.current` so this hook can read it
 * without re-rendering on every transition.
 *
 * `loadedBuild === 'dev'` short-circuits the whole thing. Local development
 * builds against a staging server (or vice versa) would otherwise
 * constantly trigger reloads on every reconnect; `dev` is the bypass.
 *
 * Returns:
 *   - onServerHello(serverBuild): call from the WS handler when the
 *       `server_hello` message arrives. Compares against loadedBuild and
 *       arms isStale if different.
 *   - onCallEnd(): call when a call ends, so we can reload during the
 *       quiet window between calls if armed.
 */
import { useCallback, useEffect, useRef } from 'react';

// 5 s after call_end before reload — long enough for the user to see the
// call-ended UI animation finish; short enough that they don't start
// poking buttons again before the refresh kicks in.
const POST_CALL_RELOAD_DELAY_MS = 5000;

// 30 minutes of staleness with no active call → reload anyway. The tablet
// likely sits idle for hours; we want it to pick up new code on a
// reasonable cadence rather than waiting for the next call.
const IDLE_RELOAD_MS = 30 * 60 * 1000;

// Daily defence-in-depth reload. 04:00 local time picks the deepest part
// of the night when no calls are happening and the user is asleep — same
// rationale as why most consumer-cloud services schedule maintenance
// windows there.
const DAILY_RELOAD_HOUR = 4;

// Window helper so tests (jsdom) and SSR don't blow up.
const safeReload = () => {
  if (typeof window !== 'undefined' && window.location && typeof window.location.reload === 'function') {
    window.location.reload();
  }
};

// Compute ms until the next occurrence of the given local-time hour.
// Pure function for unit-testability; takes `now` so tests don't have
// to monkey-patch Date.
export function msUntilNextLocalHour(targetHour, now = new Date()) {
  const next = new Date(now);
  next.setHours(targetHour, 0, 0, 0);
  if (next.getTime() <= now.getTime()) {
    next.setDate(next.getDate() + 1);
  }
  return next.getTime() - now.getTime();
}

// True if the loaded bundle and the server's reported build differ AND
// neither side is the 'dev' bypass. Pure for unit testing; the hook
// uses this to decide whether to arm `isStaleRef`.
export function isVersionMismatch(loadedBuild, serverBuild) {
  if (!loadedBuild || loadedBuild === 'dev') return false;
  if (!serverBuild || serverBuild === 'dev') return false;
  return loadedBuild !== serverBuild;
}

// Decision wrapper for the post-call-end branch: reload iff we're
// stale AND no call is currently in progress. Pure for unit testing;
// the hook uses this on the trailing edge of the 5s timer.
export function shouldReloadPostCallEnd({ isStale, isCallActive }) {
  return Boolean(isStale) && !isCallActive;
}

export default function useAutoReload({
  loadedBuild,
  isCallActiveRef,
  // Test injection points. Production never passes these.
  _reload = safeReload,
  _now = () => new Date(),
}) {
  // Whether the current bundle is stale vs the server. Ref instead of
  // state — staleness influences event handlers but doesn't drive any
  // render output (silent UX), so avoiding the re-render is purely
  // hygiene.
  const isStaleRef = useRef(false);
  // Timestamp (ms) at which staleness was first observed. Drives the
  // 30-min idle reload threshold.
  const staleSinceMsRef = useRef(null);

  // Compare server-reported build to the one we loaded with. Uses the
  // pure `isVersionMismatch` decider so the bypass + comparison rules
  // are exercised by unit tests.
  const onServerHello = useCallback((serverBuild) => {
    if (isVersionMismatch(loadedBuild, serverBuild)) {
      if (!isStaleRef.current) {
        isStaleRef.current = true;
        staleSinceMsRef.current = _now().getTime();
        // eslint-disable-next-line no-console
        console.info(
          `[#642 useAutoReload] bundle is stale (loaded=${loadedBuild} server=${serverBuild}) — armed for silent reload during next idle window`
        );
      }
    } else if (loadedBuild && serverBuild && loadedBuild === serverBuild) {
      // Server matches us — explicitly clear, in case we previously
      // hit a transient mismatch (e.g. a rolling deploy where two
      // server containers briefly had different versions).
      isStaleRef.current = false;
      staleSinceMsRef.current = null;
    }
    // The `dev` bypass branch (handled inside isVersionMismatch)
    // intentionally does nothing — neither arms nor clears, so a real
    // SHA loaded against a real server is unaffected by a stray
    // dev-server reconnect.
  }, [loadedBuild, _now]);

  // Called by the WS handler when a call ends. Defer the reload by
  // POST_CALL_RELOAD_DELAY_MS so the user perceives the call-ended UI
  // before the screen blinks.
  const onCallEnd = useCallback(() => {
    if (!isStaleRef.current) return;
    setTimeout(() => {
      // Re-check on the trailing edge — another call could have started
      // in the 5s gap (very unlikely but cheap to guard). Uses the pure
      // `shouldReloadPostCallEnd` decider for testability.
      if (shouldReloadPostCallEnd({
        isStale: isStaleRef.current,
        isCallActive: isCallActiveRef.current,
      })) {
        // eslint-disable-next-line no-console
        console.info('[#642 useAutoReload] reloading post-call-end (stale + idle)');
        _reload();
      }
    }, POST_CALL_RELOAD_DELAY_MS);
  }, [isCallActiveRef, _reload]);

  // pageshow with persisted=true === tab restored from bfcache. The
  // user JUST returned to the app — reload immediately if stale. This
  // is the single highest-value branch in the whole hook: it's almost
  // certainly the cause of the cache-pain Bas hit all week. Cache
  // headers can't help here because there's no network request on a
  // bfcache restore.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPageShow = (e) => {
      if (e && e.persisted === true && isStaleRef.current && !isCallActiveRef.current) {
        // eslint-disable-next-line no-console
        console.info('[#642 useAutoReload] reloading on pageshow (bfcache restore + stale)');
        _reload();
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [isCallActiveRef, _reload]);

  // Idle ceiling: if we've been stale for > IDLE_RELOAD_MS with no
  // active call, reload anyway. Poll every minute — cheap, and the
  // exact moment within the minute doesn't matter (the user is idle
  // by definition).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const interval = setInterval(() => {
      if (!isStaleRef.current || !staleSinceMsRef.current) return;
      if (isCallActiveRef.current) return;
      const elapsed = _now().getTime() - staleSinceMsRef.current;
      if (elapsed >= IDLE_RELOAD_MS) {
        // eslint-disable-next-line no-console
        console.info(`[#642 useAutoReload] reloading after ${Math.round(elapsed / 60000)}min of idle staleness`);
        _reload();
      }
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [isCallActiveRef, _reload, _now]);

  // Daily defence-in-depth reload. Runs even when isStale is false —
  // the point is to catch the case where staleness was never detected
  // (WS never reconnected after deploy, server_hello never arrived, etc.).
  // Skips if a call is active when the timer fires; tries again the
  // next day (so worst case we miss a day, no big deal).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let cancelled = false;

    const scheduleNext = () => {
      const delay = msUntilNextLocalHour(DAILY_RELOAD_HOUR, _now());
      const timer = setTimeout(() => {
        if (cancelled) return;
        if (!isCallActiveRef.current) {
          // eslint-disable-next-line no-console
          console.info('[#642 useAutoReload] reloading on daily 04:00 schedule (defence in depth)');
          _reload();
          // Note: page reloads, so the setTimeout chain below doesn't matter.
          return;
        }
        // Active call at 04:00 — extremely rare for an elderly user.
        // Skip this round and reschedule for tomorrow.
        // eslint-disable-next-line no-console
        console.info('[#642 useAutoReload] daily reload skipped (call active); rescheduled for tomorrow');
        scheduleNext();
      }, delay);
      return () => clearTimeout(timer);
    };

    const cleanup = scheduleNext();
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [isCallActiveRef, _reload, _now]);

  return { onServerHello, onCallEnd };
}
